"use client";

import React, { useMemo } from "react";
import { ArrowLeft, MapPin, Info, ExternalLink } from "lucide-react";
import { PostsProvider, usePosts, type Post } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";





function RelatedSitesConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-64 w-full animate-pulse rounded-xl bg-muted/20" />
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => {
        const image = post.customFields?.featured_image || post.featuredImage || "/img/placeholder_site.png";
        const category = post.customFields?.category;

        return (
          <div key={post.id} data-index={i} className="group flex flex-col h-full">
            <Link to={post.link || "#"} className="block h-full">
              <Card className="h-full overflow-hidden border-transparent shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-1 bg-card">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={image}
                    alt={post.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  {category && (
                    <Badge className="absolute top-4 right-4 bg-white/90 text-primary hover:bg-white shadow-sm backdrop-blur-sm">
                      {category}
                    </Badge>
                  )}
                </div>
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-lg text-primary line-clamp-1">
                    {post.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <span className="group-hover:text-accent transition-colors font-medium">
                      Explore Site &rarr;
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
        );
      })}
    </div>
  );
}

function RelatedSitesSection({ idsString }: { idsString: string }) {
  
  const relatedQuery = useMemo(() => {
    if (!idsString) return null;
    
    const ids = idsString
      .split(",")
      .map((id) => parseInt(id.trim(), 10))
      .filter((id) => !isNaN(id) && id > 0);

    if (ids.length === 0) return null;

    return new WP_Query({
      post_type: "site",
      post__in: ids,
      per_page: ids.length,
      orderby: "post__in", 
    });
  }, [idsString]);

  if (!relatedQuery) return null;

  return (
    <div className="mt-24 border-t border-border pt-16">
      <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="font-heading text-3xl font-bold text-primary">
            You Might Also Like
          </h2>
          <p className="mt-2 text-lg text-muted-foreground font-sans">
            Discover other unforgettable destinations nearby.
          </p>
        </div>
      </div>

      <PostsProvider wp_query={relatedQuery}>
        <RelatedSitesConsumer />
      </PostsProvider>
    </div>
  );
}





function SingleSiteConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-20">
        <div className="h-96 w-full animate-pulse rounded-3xl bg-muted/20 mb-12" />
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-8 space-y-6">
            <div className="h-12 w-3/4 animate-pulse rounded-lg bg-muted/20" />
            <div className="h-6 w-full animate-pulse rounded bg-muted/20" />
            <div className="h-6 w-full animate-pulse rounded bg-muted/20" />
            <div className="h-6 w-2/3 animate-pulse rounded bg-muted/20" />
          </div>
          <div className="lg:col-span-4">
            <div className="h-64 w-full animate-pulse rounded-xl bg-muted/20" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold text-destructive mb-4">Error Loading Site</h2>
        <p className="text-muted-foreground mb-6">We couldn't load the site details. Please try again.</p>
        <Button onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold text-primary mb-4">Site Not Found</h2>
        <p className="text-muted-foreground mb-6">The requested site could not be found.</p>
        <Link to="/SitesGridArchitecturePage">
          <Button>Back to All Sites</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const customFields = post.customFields || {};
  
  
  const featuredImage = customFields.featured_image || post.featuredImage || "/img/placeholder_hero.png";
  const content = customFields.content || post.content || "";
  const category = customFields.category;
  const relatedSitesIds = customFields.related_sites || "";

  return (
    <article className="animate-in fade-in duration-700">
      {}
      <div className="relative w-full bg-primary/5">
        <div className="relative h-[50vh] min-h-[400px] w-full overflow-hidden lg:h-[60vh]">
          <img
            src={featuredImage}
            alt={post.title}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/20 to-transparent" />
          
          <div className="absolute bottom-0 left-0 w-full p-6 pb-12 md:p-12 md:pb-16">
            <div className="container mx-auto">
              <div className="max-w-4xl">
                {category && (
                  <Badge className="mb-4 bg-accent text-accent-foreground hover:bg-accent/90 border-none px-3 py-1 text-sm uppercase tracking-wider shadow-lg">
                    {category}
                  </Badge>
                )}
                <h1 className="font-heading text-4xl font-bold text-white shadow-black/10 drop-shadow-md md:text-5xl lg:text-6xl">
                  {post.title}
                </h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="container mx-auto px-4 py-16 md:py-24">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          
          {}
          <div className="lg:col-span-8">
            <div className="prose prose-lg max-w-none text-foreground/80 prose-headings:font-heading prose-headings:text-primary prose-a:text-accent prose-a:no-underline hover:prose-a:underline prose-img:rounded-xl">
              {}
              <div className="whitespace-pre-wrap font-sans text-lg leading-relaxed">
                {content}
              </div>
            </div>
          </div>

          {}
          <aside className="lg:col-span-4 space-y-8">
            {}
            <Card className="overflow-hidden border-border/60 bg-sidebar shadow-lg">
              <CardHeader className="bg-primary/5 pb-4">
                <CardTitle className="flex items-center gap-2 font-heading text-xl text-primary">
                  <Info className="h-5 w-5 text-accent" />
                  Site Details
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 pt-6">
                {category && (
                  <div className="flex items-start gap-3">
                    <MapPin className="mt-1 h-5 w-5 shrink-0 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Region / Category</p>
                      <p className="font-medium text-foreground">{category}</p>
                    </div>
                  </div>
                )}
                
                <Separator className="my-2" />
                
                <div className="flex flex-col gap-3">
                  <Link to="/ContactPage" className="w-full">
                    <Button className="w-full bg-primary text-white hover:bg-primary/90" size="lg">
                      Book a Tour Here
                    </Button>
                  </Link>
                  <Link to="/SitesGridArchitecturePage" className="w-full">
                    <Button variant="outline" className="w-full border-primary/20 text-primary hover:bg-primary/5 hover:text-primary">
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back to Sites
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {}
            <div className="rounded-xl bg-accent/10 p-6 border border-accent/20">
              <h3 className="font-heading text-lg font-bold text-primary mb-2">
                Need Help Planning?
              </h3>
              <p className="text-muted-foreground mb-4">
                Our experts can help you build the perfect itinerary including {post.title}.
              </p>
              <Link to="/ContactPage">
                <Button variant="link" className="p-0 text-accent hover:text-accent/80 font-semibold h-auto">
                  Contact Us <ExternalLink className="ml-1 h-3 w-3" />
                </Button>
              </Link>
            </div>
          </aside>
        </div>

        {}
        {relatedSitesIds && <RelatedSitesSection idsString={relatedSitesIds} />}
      </div>
    </article>
  );
}





export default function SingleSiteTemplateSection() {
  
  
  const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();

  
  
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-site-template-section" className="bg-background min-h-screen">
      <PostsProvider wp_query={wp_query}>
        <SingleSiteConsumer />
      </PostsProvider>
    </section>
  );
}